import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../context/Auth/AuthContext';
import api from '../services/api';
import { playTaskNotification } from '../utils/notificationSound';
import { toast } from 'react-toastify';

export const useTaskNotifications = () => {
  const { user } = useContext(AuthContext);
  const [notificationCount, setNotificationCount] = useState(0);
  const [pendingReminders, setPendingReminders] = useState([]);
  const lastNotificationCheck = useRef(new Date());

  // Buscar tarefas pendentes com lembretes
  const fetchPendingReminders = async () => {
    try {
      const now = new Date();
      
      // Definir início e fim do dia atual
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      
      const response = await api.get('/tasks', {
        params: {
          status: 'pending',
          dueDateFrom: startOfDay.toISOString(),
          dueDateTo: endOfDay.toISOString(),
          pageNumber: 1,
          sortBy: 'reminderDate',
          sortOrder: 'ASC'
        }
      });

      const tasks = response.data.tasks || [];
      
      // Debug: mostrar todas as tarefas com lembretes habilitados
      const tasksWithReminders = tasks.filter(task => task.reminderEnabled);
      
      // Filtrar tarefas com lembretes ativos que devem aparecer no badge
      // Critério: lembrete habilitado, data já passou, tarefa ainda pendente, e é de hoje
      const activeReminders = tasks.filter(task => {
        if (!task.reminderEnabled || !task.reminderDate || task.status !== 'pending') {
          return false;
        }
        
        const reminderTime = new Date(task.reminderDate);
        const isReminderTime = reminderTime <= now;
        
        // Verificar se a tarefa é de hoje (data de vencimento)
        const taskDueDate = new Date(task.dueDate);
        const isToday = taskDueDate >= startOfDay && taskDueDate <= endOfDay;
        
        // Mostrar no badge se o tempo do lembrete já passou E a tarefa é de hoje
        // NÃO importa se reminderSent = true, pois o badge deve mostrar lembretes ativos
        return isReminderTime && isToday;
      });

      // Debug: mostrar quais tarefas estão sendo contadas para o badge
      if (activeReminders.length > 0) {
        activeReminders.forEach(task => {
          // Task info available for debugging if needed
        });
      }

      // Para tocar som, verificar apenas lembretes que não foram enviados ainda
      const unsentReminders = activeReminders.filter(task => !task.reminderSent);
      
      // Verificar se há novos lembretes desde a última verificação
      const newReminders = unsentReminders.filter(task => {
        const reminderTime = new Date(task.reminderDate);
        return reminderTime > lastNotificationCheck.current && reminderTime <= now;
      });

      // Tocar som e mostrar notificação para novos lembretes
      if (newReminders.length > 0) {
        playTaskNotification();
        
        newReminders.forEach(task => {
          toast.info(`🔔 Lembrete: ${task.title}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            toastId: `reminder-${task.id}` // Evitar duplicatas
          });
        });
      }

      const previousCount = notificationCount;
      setPendingReminders(activeReminders);
      setNotificationCount(activeReminders.length);
      
      lastNotificationCheck.current = now;

    } catch (error) {
      console.error('Erro ao buscar lembretes pendentes:', error);
    }
  };

  // Atualizar a cada minuto e também quando solicitado manualmente
  useEffect(() => {
    if (!user) return;

    fetchPendingReminders();
    
    // Verificação normal a cada minuto  
    const interval = setInterval(() => {
      fetchPendingReminders();
    }, 60000); // 1 minuto

    // Adicionar listener para eventos personalizados de atualização
    const handleTaskUpdate = () => {
      setTimeout(() => fetchPendingReminders(), 100); // Pequeno delay para garantir que a API atualizou
    };

    // Adicionar listener para quando a página recebe foco (capturar mudanças em background)
    const handlePageFocus = () => {
      fetchPendingReminders();
    };

    window.addEventListener('taskUpdated', handleTaskUpdate);
    window.addEventListener('taskCreated', handleTaskUpdate);
    window.addEventListener('focus', handlePageFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('taskUpdated', handleTaskUpdate);
      window.removeEventListener('taskCreated', handleTaskUpdate);
      window.removeEventListener('focus', handlePageFocus);
    };
  }, [user]);

  // Verificação mais frequente quando há lembretes próximos (separado para evitar loop)
  useEffect(() => {
    if (!user || pendingReminders.length === 0) return;

    const quickInterval = setInterval(() => {
      const now = new Date();
      const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60000);
      
      // Se há lembretes nas próximas 5 minutos, verificar mais frequentemente
      const hasUpcomingReminders = pendingReminders.some(task => {
        if (!task.reminderDate) return false;
        const reminderTime = new Date(task.reminderDate);
        return reminderTime <= fiveMinutesFromNow && reminderTime > now;
      });
      
      if (hasUpcomingReminders) {
        fetchPendingReminders();
      }
    }, 10000); // 10 segundos

    return () => clearInterval(quickInterval);
  }, [pendingReminders, user]);

  return {
    notificationCount,
    pendingReminders,
    refreshNotifications: fetchPendingReminders,
    forceRefresh: () => {
      fetchPendingReminders();
    }
  };
};

export default useTaskNotifications;
